/**
 * OPERATIONS PANEL
 *
 * Toggle overlay showing real-time agent activity,
 * cost tracking, and operation status.
 */

import { useState, useMemo } from "react";
import { useWorldStore } from "@/stores/worldStore";
import {
  Activity, X, AlertTriangle, Play, Clock, CheckCircle,
  ChevronRight, Zap, DollarSign, TrendingUp
} from "lucide-react";

export function OperationsPanel() {
  const agents = useWorldStore((s) => s.agents);
  const projects = useWorldStore((s) => s.projects);
  const [isOpen, setIsOpen] = useState(false);

  // Group agents by state
  const grouped = useMemo(() => {
    const running = agents.filter(a => a.state === "busy");
    const pending = agents.filter(a => a.state === "queued");
    const blocked = agents.filter(a =>
      a.attentionState === "stuck" || a.attentionState === "error"
    );
    const finished = agents.filter(a =>
      a.state === "idle" && (a.attentionState === "completed" || (!a.attentionState || a.attentionState === "none"))
    );
    return { running, pending, blocked, finished };
  }, [agents]);

  const alertCount = grouped.blocked.length;

  // Helper to get project name for an agent
  const getProjectName = (projectId: number) => {
    return projects.find(p => p.id === projectId)?.name || "Unknown";
  };

  // Agent type avatar component
  const AgentAvatar = ({ state, size = 28 }: { state: string; size?: number }) => {
    const colors: Record<string, { bg: string; ring: string; icon: string }> = {
      busy: { bg: "rgba(59,130,246,0.15)", ring: "rgba(59,130,246,0.3)", icon: "#3B82F6" },
      queued: { bg: "rgba(168,85,247,0.15)", ring: "rgba(168,85,247,0.3)", icon: "#A855F7" },
      idle: { bg: "rgba(148,163,184,0.15)", ring: "rgba(148,163,184,0.3)", icon: "#94A3B8" },
      stuck: { bg: "rgba(239,68,68,0.15)", ring: "rgba(239,68,68,0.3)", icon: "#EF4444" },
      error: { bg: "rgba(239,68,68,0.15)", ring: "rgba(239,68,68,0.3)", icon: "#EF4444" },
    };
    const c = colors[state] || colors.idle;
    return (
      <div
        className="rounded-full flex items-center justify-center flex-shrink-0"
        style={{ width: size, height: size, background: c.bg, border: `1px solid ${c.ring}` }}
      >
        <Zap className="w-3 h-3" style={{ color: c.icon }} />
      </div>
    );
  };

  // Format elapsed time
  const formatElapsed = (startedAt?: string) => {
    if (!startedAt) return "";
    // Simple display - just show the startedAt string
    return startedAt;
  };

  return (
    <>
      {/* Toggle Pill — left side, below Colony Overview */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="absolute top-[168px] left-4 z-50 flex items-center gap-2 px-3 py-1.5 rounded-full transition-all"
        style={{
          background: isOpen ? 'rgba(0,178,200,0.15)' : 'rgba(26,26,30,0.85)',
          backdropFilter: 'blur(16px)',
          border: `1px solid ${isOpen ? 'rgba(0,178,200,0.3)' : 'rgba(255,255,255,0.08)'}`,
        }}
        data-testid="button-toggle-operations"
      >
        <Activity className="w-3.5 h-3.5" style={{ color: isOpen ? '#00B2C8' : '#9CA3AF' }} />
        <span className="text-[11px] font-medium" style={{ color: isOpen ? '#00B2C8' : '#9CA3AF' }}>
          Operations
        </span>
        {alertCount > 0 && (
          <span className="flex items-center justify-center w-4 h-4 rounded-full bg-red-500/20 text-red-400 text-[10px] font-bold">
            {alertCount}
          </span>
        )}
      </button>

      {/* Panel */}
      {isOpen && (
        <div
          className="absolute top-0 left-0 w-[320px] h-full z-[60] flex flex-col"
          style={{
            background: '#1A1A1E',
            borderRight: '1px solid rgba(255,255,255,0.08)',
            animation: 'slideInLeft 0.2s ease-out',
          }}
          data-testid="panel-operations"
        >
          {/* Header */}
          <div className="h-11 flex items-center justify-between px-4 border-b border-[rgba(255,255,255,0.06)] flex-shrink-0">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-[#00B2C8]" />
              <span className="text-[13px] font-semibold text-[#F3F4F6]">Operations</span>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 rounded hover:bg-[rgba(255,255,255,0.06)] transition-colors"
              data-testid="button-close-operations"
              aria-label="Close operations panel"
            >
              <X className="w-4 h-4 text-[#6B7280]" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#2A2A2F transparent' }}>

            {/* Cost Ticker */}
            <div className="px-4 py-3 border-b border-[rgba(255,255,255,0.06)]">
              <div className="flex items-center gap-1.5 mb-2">
                <DollarSign className="w-3 h-3 text-[#6B7280]" />
                <span className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider">Cost Today</span>
              </div>
              <div className="flex items-baseline gap-3">
                <span className="text-xl font-bold text-[#F3F4F6]">$0.00</span>
                <span className="text-[11px] text-[#6B7280] flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  $0.00/hr
                </span>
              </div>
              {/* Sparkline placeholder */}
              <div className="mt-2 h-8 rounded bg-[rgba(255,255,255,0.03)] flex items-end px-1 gap-px">
                {Array.from({ length: 24 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex-1 rounded-t bg-[#00B2C8]/20"
                    style={{ height: `${Math.random() * 60 + 10}%` }}
                  />
                ))}
              </div>
            </div>

            {/* Blocked Agents */}
            {grouped.blocked.length > 0 && (
              <div className="px-4 py-3 border-b border-[rgba(255,255,255,0.06)]">
                <div className="flex items-center gap-1.5 mb-2">
                  <AlertTriangle className="w-3 h-3 text-red-400" />
                  <span className="text-[10px] font-semibold text-red-400 uppercase tracking-wider">
                    Blocked ({grouped.blocked.length})
                  </span>
                </div>
                <div className="space-y-1.5">
                  {grouped.blocked.map(agent => (
                    <div key={agent.id} className="flex items-center gap-2 p-2 rounded-md bg-red-500/5 border border-red-500/10">
                      <AgentAvatar state="stuck" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-medium text-[#F3F4F6] truncate">
                          {agent.name || `Agent ${agent.id}`}
                        </div>
                        <div className="text-[10px] text-red-400/80 truncate">
                          {agent.attentionMessage || "Blocked on dependency"}
                        </div>
                      </div>
                      <ChevronRight className="w-3 h-3 text-[#4B5563] flex-shrink-0" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Running Agents */}
            <div className="px-4 py-3 border-b border-[rgba(255,255,255,0.06)]">
              <div className="flex items-center gap-1.5 mb-2">
                <Play className="w-3 h-3 text-blue-400" />
                <span className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider">
                  Running ({grouped.running.length})
                </span>
              </div>
              {grouped.running.length === 0 ? (
                <div className="text-[11px] text-[#4B5563] py-2">No agents running</div>
              ) : (
                <div className="space-y-1.5">
                  {grouped.running.map(agent => (
                    <div key={agent.id} className="flex items-center gap-2 p-2 rounded-md hover:bg-[rgba(255,255,255,0.03)] transition-colors">
                      <AgentAvatar state="busy" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-medium text-[#F3F4F6] truncate">
                          {agent.name || `Agent ${agent.id}`}
                        </div>
                        <div className="text-[10px] text-[#6B7280] truncate">
                          {getProjectName(agent.projectId)} {agent.pipelineStage && `· ${agent.pipelineStage}`}
                        </div>
                      </div>
                      {agent.startedAt && (
                        <span className="text-[10px] text-blue-400/70 flex-shrink-0 flex items-center gap-1">
                          <Clock className="w-2.5 h-2.5" />
                          {formatElapsed(agent.startedAt)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pending Agents */}
            <div className="px-4 py-3 border-b border-[rgba(255,255,255,0.06)]">
              <div className="flex items-center gap-1.5 mb-2">
                <Clock className="w-3 h-3 text-purple-400" />
                <span className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider">
                  Pending ({grouped.pending.length})
                </span>
              </div>
              {grouped.pending.length === 0 ? (
                <div className="text-[11px] text-[#4B5563] py-2">No agents queued</div>
              ) : (
                <div className="space-y-1.5">
                  {grouped.pending.map(agent => (
                    <div key={agent.id} className="flex items-center gap-2 p-2 rounded-md hover:bg-[rgba(255,255,255,0.03)] transition-colors">
                      <AgentAvatar state="queued" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-medium text-[#F3F4F6] truncate">
                          {agent.name || `Agent ${agent.id}`}
                        </div>
                        <div className="text-[10px] text-[#6B7280] truncate">
                          {getProjectName(agent.projectId)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Finished Agents */}
            <div className="px-4 py-3">
              <div className="flex items-center gap-1.5 mb-2">
                <CheckCircle className="w-3 h-3 text-green-400" />
                <span className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider">
                  Finished ({grouped.finished.length})
                </span>
              </div>
              {grouped.finished.length === 0 ? (
                <div className="text-[11px] text-[#4B5563] py-2">No completed agents</div>
              ) : (
                <div className="space-y-1.5">
                  {grouped.finished.slice(0, 10).map(agent => (
                    <div key={agent.id} className="flex items-center gap-2 p-2 rounded-md hover:bg-[rgba(255,255,255,0.03)] transition-colors opacity-70">
                      <AgentAvatar state="idle" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-medium text-[#D1D5DB] truncate">
                          {agent.name || `Agent ${agent.id}`}
                        </div>
                        <div className="text-[10px] text-[#4B5563] truncate">
                          {getProjectName(agent.projectId)}
                        </div>
                      </div>
                    </div>
                  ))}
                  {grouped.finished.length > 10 && (
                    <div className="text-[11px] text-[#4B5563] text-center py-1">
                      +{grouped.finished.length - 10} more
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Bottom Action */}
          {grouped.pending.length > 0 && (
            <div className="px-4 py-3 border-t border-[rgba(255,255,255,0.06)] flex-shrink-0">
              <button
                className="w-full py-2 rounded-md text-[12px] font-medium transition-colors"
                style={{
                  background: 'rgba(0,178,200,0.12)',
                  color: '#00B2C8',
                  border: '1px solid rgba(0,178,200,0.2)',
                }}
                data-testid="button-approve-all-pending"
              >
                Approve All Pending ({grouped.pending.length})
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
